// useAccountVault — account-level vault state shared across the app.
//
// The vault is an ECDH keypair (account-vault-v2):
//   • the PUBLIC half is stored in the clear → creating or importing a wallet
//     encrypts the seed to it with NO password and NO unlock, ever;
//   • the PRIVATE half is wrapped in password/recovery slots → only revealing a
//     seed (and later, signing) asks for the password.
//
// Legacy accounts hold a v1 keyring (symmetric DEK). Those still need an unlock
// before a seed can be written, because with a DEK the same key does both.
// The `canEncrypt` flag tells the UI which world it is in.
//
// Key material never enters serializable app state. Preview may persist the
// non-extractable v2 CryptoKey in an origin-bound device slot so reloads do not
// create a second login prompt; raw private bytes and seeds remain unavailable.

import { atom, useAtom } from 'jotai'
import { useCallback, useEffect, useRef } from 'react'
import {
  type AnyAccountKeyring,
  createAccountVault,
  enrollAccountLoginWrap,
  fetchAccountVault,
  fetchAuthSession,
  getSessionAccountId,
  getSessionToken,
  onSessionChanged,
  rotateAccountLoginWrap,
  updateAccountVaultPasswordSlot,
} from '../api/userland'
import {
  type DeviceSigningIdentity,
  forgetDeviceSigningKey,
  loadDevicePkcs8Bytes,
  loadDeviceSigningKey,
  saveDeviceSigningKey,
} from '../lib/accountDeviceSigning'
import {
  type AccountSecretEnvelope,
  AccountUnlockSession,
  AccountVaultSecretMismatchError,
  AccountVaultSession,
  type DecryptSeedOptions,
  type PreparedAccountVaultPassword,
  addLoginSlotToKeyring,
  createAccountKeyringV2WithPreparedPassword,
  encryptSeedToAccountVault,
  generateLoginSecret,
  prepareAccountVaultPassword,
  rewrapAccountKeyringV2PasswordWithPrepared,
  rotateAccountKeyringV2LoginSlot,
  unlockAccountKeyring,
  unlockAccountKeyringV2,
} from '../lib/clientVault'

export type AccountVaultStatus =
  | 'unknown'
  | 'offline'
  | 'none' // no keyring yet — set one up
  | 'locked' // keyring known; private half not unwrapped
  | 'unlocked' // private half in memory

// Sessions hold raw key material — module-level, never inside an atom (atoms
// can be devtools-serialized).
let v2Session: AccountUnlockSession | null = null
let v1Session: AccountVaultSession | null = null
let unlockedSessionToken: string | null = null
let unlockedAccountID: string | null = null
let preparedAccountPassword: PreparedAccountVaultPassword | null = null

const statusAtom = atom<AccountVaultStatus>('unknown')
type AccountPasswordPurpose = 'none' | 'setup' | 'migration'
type AccountPasswordState = {
  sessionToken: string | null
  purpose: AccountPasswordPurpose
}
const accountPasswordAtom = atom<AccountPasswordState>({ sessionToken: null, purpose: 'none' })
type AccountKeyringState = {
  keyring: AnyAccountKeyring | null
  sessionToken: string | null
  accountID: string | null
}
const keyringAtom = atom<AccountKeyringState>({
  keyring: null,
  sessionToken: null,
  accountID: null,
})

export type UseAccountVault = {
  status: AccountVaultStatus
  /** True when a seed can be encrypted right now, no password needed (v2). */
  canEncrypt: boolean
  /** True when the private half is actually in memory and can sign. */
  canSign: boolean
  /** Successful account login is bound to pending first-time vault setup. */
  accountPasswordReady: boolean
  /** Existing v2 slot must be re-wrapped once from its old vault password. */
  migrationRequired: boolean
  /** v1 keyrings retain their historical explicit vault-password UX. */
  legacy: boolean
  reload: () => Promise<AccountVaultStatus>
  /** Session login wrap + this-device key. Seed confirm uses this, not recovery. */
  unlockFromSession: () => Promise<AccountVaultStatus>
  setup: (recoveryKey: string) => Promise<void>
  confirmAccountPassword: (password: string) => Promise<void>
  /** Re-wraps the password slot to the account password just confirmed. The
   *  slot is opened with the old password (a string) or another secret: after
   *  a reset by email code, the recovery key. */
  migratePassword: (
    oldSecret: string | { password?: string; recoveryKey?: string }
  ) => Promise<void>
  /** Makes the password slot open with `newPassword`, in one operation: it
   *  confirms the new password and, when the slot still takes another secret,
   *  re-wraps it from `from` (the old password, the recovery key or a login
   *  secret). Without `from` it only reports that a secret is needed. Use it
   *  after the account password changed on the server (change, reset): two
   *  separate calls from one render see stale state. */
  setPasswordSlot: (newPassword: string, from?: DecryptSeedOptions) => Promise<PasswordSlotResult>
  /** After a password reset released the login secret: replaces the login
   *  slot with one under a new secret, so the released one opens nothing.
   *  The wallet stays unlocked either way. */
  rotateLoginSecret: (oldLoginSecret: Uint8Array) => Promise<void>
  unlock: (secret: {
    password?: string
    recoveryKey?: string
    passkeyPrf?: Uint8Array
    loginSecret?: Uint8Array
  }) => Promise<void>
  lock: () => Promise<void>
  forgetDevice: () => Promise<void>
  encryptSeed: (seedWords: string) => Promise<AccountSecretEnvelope>
  decryptSeed: (envelope: AccountSecretEnvelope) => Promise<string>
}

/** 'unlocked': the slot opens with the new password; 'none': the account has
 *  no vault; 'needs-secret': the slot takes another secret and none was given;
 *  'legacy': a v1 vault, which has no password slot to re-wrap. */
export type PasswordSlotResult = 'unlocked' | 'none' | 'needs-secret' | 'legacy'

export class AccountVaultMigrationCommittedError extends Error {
  constructor() {
    super('wallet security updated; account confirmation is required again')
    this.name = 'AccountVaultMigrationCommittedError'
  }
}

export class AccountVaultSetupCommittedError extends Error {
  constructor() {
    super('wallet security created; account confirmation is required again')
    this.name = 'AccountVaultSetupCommittedError'
  }
}

export class AccountVaultPasswordConfirmationRequiredError extends Error {
  constructor() {
    super('v2 password unlock requires account confirmation')
    this.name = 'AccountVaultPasswordConfirmationRequiredError'
  }
}

function memoryUnlocked(): boolean {
  return Boolean(v2Session?.isUnlocked() || v1Session?.isUnlocked())
}

function rebindUnlockedSession(sessionToken: string | null, accountID: string | null): void {
  unlockedSessionToken = sessionToken
  if (accountID) unlockedAccountID = accountID
}

function sessionUnlocked(sessionToken: string | null): boolean {
  return memoryUnlocked() && unlockedSessionToken === sessionToken
}

function sameAccountMemoryUnlocked(accountID?: string | null): boolean {
  if (!memoryUnlocked()) return false
  const current = getSessionAccountId()
  if (unlockedAccountID && current && unlockedAccountID !== current) return false
  if (accountID && current && accountID !== current) return false
  if (accountID && unlockedAccountID && accountID !== unlockedAccountID) return false
  return true
}

function keepSameAccountUnlock(accountID: string | null): AccountVaultStatus | null {
  if (!sameAccountMemoryUnlocked(accountID)) return null
  rebindUnlockedSession(getSessionToken(), getSessionAccountId() ?? accountID)
  return 'unlocked'
}

function lockVaultSessions(): void {
  v2Session?.lock()
  v1Session?.lock()
  v2Session = null
  v1Session = null
  unlockedSessionToken = null
  unlockedAccountID = null
}

function discardPreparedAccountPassword(): void {
  preparedAccountPassword?.dispose()
  preparedAccountPassword = null
}

export function resetAccountVaultForTest(): void {
  lockVaultSessions()
  discardPreparedAccountPassword()
}

function accountPasswordReady(
  state: AccountPasswordState,
  purpose: Exclude<AccountPasswordPurpose, 'none'>
): boolean {
  if (
    state.sessionToken !== getSessionToken() ||
    state.purpose !== purpose ||
    !preparedAccountPassword?.isUsable()
  ) {
    return false
  }
  return true
}

function deviceSigningEnabled(): boolean {
  return true
}

function deviceIdentity(
  accountID: string | null,
  sessionToken: string | null,
  keyring: AnyAccountKeyring | null
): DeviceSigningIdentity | null {
  if (!deviceSigningEnabled() || !accountID || keyring?.kind !== 'v2') return null
  return { accountID, sessionToken, vaultPublicKeyB64: keyring.publicKeyB64 }
}

function assertSessionUnchanged(sessionToken: string | null): void {
  if (getSessionToken() !== sessionToken) throw new Error('account session changed')
}

function assertOperationCurrent(alive: boolean, sessionToken: string | null): void {
  if (!alive) throw new Error('account vault operation cancelled')
  assertSessionUnchanged(sessionToken)
}

function sessionBindingDrifted(captured: string | null): boolean {
  const current = getSessionToken()
  if (captured === current) return false
  // Login or TON Connect reconnect: session id appears or rotates. Keep the
  // in-flight device-key restore. Logout (token gone) still aborts.
  if (current) return false
  return Boolean(captured)
}

function reloadIsStale(
  alive: boolean,
  generation: number,
  currentGeneration: number,
  sessionToken: string | null,
  capturedAccount: string | null
): boolean {
  if (!alive || generation !== currentGeneration) return true
  if (sessionBindingDrifted(sessionToken)) return true
  const currentAccount = getSessionAccountId()
  if (capturedAccount && currentAccount && capturedAccount !== currentAccount) return true
  return false
}

function requireCurrentUnlockedSession(): AccountUnlockSession | AccountVaultSession {
  const session = v2Session ?? v1Session
  const currentAccount = getSessionAccountId()
  if (!session?.isUnlocked()) {
    lockVaultSessions()
    throw new Error('account vault is locked')
  }
  if (unlockedAccountID && currentAccount && unlockedAccountID !== currentAccount) {
    lockVaultSessions()
    throw new Error('account vault is locked')
  }
  rebindUnlockedSession(getSessionToken(), currentAccount)
  return session
}

type ReloadVaultOutcome =
  | { status: 'none' | 'locked' | 'unlocked' }
  | { status: 'restored'; privateKey: CryptoKey }

function readVaultFetch(fetched: unknown): {
  keyring: AnyAccountKeyring | null
  loginSecret?: Uint8Array
} {
  if (!fetched || typeof fetched !== 'object') return { keyring: null }
  const value = fetched as { keyring?: AnyAccountKeyring; loginSecret?: Uint8Array; kind?: string }
  if (value.keyring && (value.keyring.kind === 'v1' || value.keyring.kind === 'v2')) {
    return { keyring: value.keyring, loginSecret: value.loginSecret }
  }
  if (value.kind === 'v1' || value.kind === 'v2') {
    return { keyring: fetched as AnyAccountKeyring }
  }
  return { keyring: null }
}

async function reloadVaultOutcome(
  loaded: AnyAccountKeyring | null,
  accountID: string,
  sessionToken: string | null,
  loginSecret?: Uint8Array
): Promise<ReloadVaultOutcome> {
  if (loaded === null) return { status: 'none' }
  // Telegram/Google re-login rotates the session id while this fetch is in
  // flight. The captured token then misses sessionUnlocked() and a PKCS8/wrap
  // miss would report locked even though the private key is still in RAM.
  if (sameAccountMemoryUnlocked(accountID) || sessionUnlocked(sessionToken)) {
    return { status: 'unlocked' }
  }
  const identity = deviceIdentity(accountID, sessionToken, loaded)
  const privateKey = identity ? await loadDeviceSigningKey(identity) : null
  if (privateKey) return { status: 'restored', privateKey }
  if (loginSecret && loaded.kind === 'v2') {
    try {
      const unwrapped = await unlockAccountKeyringV2(loaded, { loginSecret })
      return { status: 'restored', privateKey: unwrapped }
    } catch {
      // login wrap present but does not match this keyring
    }
  }
  return { status: 'locked' }
}

async function enrollLoginWrapIfNeeded(keyring: AnyAccountKeyring | null): Promise<void> {
  if (!keyring || keyring.kind !== 'v2') return
  if (keyring.keySlots.some((slot) => slot.kind === 'login')) return
  const pkcs8 = await loadDevicePkcs8Bytes(keyring.publicKeyB64)
  if (!pkcs8) return
  try {
    const loginSecret = generateLoginSecret()
    const next = await addLoginSlotToKeyring(keyring, pkcs8, loginSecret)
    const loginSlot = next.keySlots.find((slot) => slot.kind === 'login')
    if (!loginSlot) return
    await enrollAccountLoginWrap({
      publicKeyB64: keyring.publicKeyB64,
      loginSlot,
      loginSecret,
    })
  } catch {
    // Existing device can keep trading; a later session retries enroll.
  } finally {
    pkcs8.fill(0)
  }
}

function applyReloadVaultOutcome(
  outcome: ReloadVaultOutcome,
  sessionToken: string | null,
  accountID: string | null
): void {
  if (outcome.status === 'restored') {
    lockVaultSessions()
    v2Session = new AccountUnlockSession(outcome.privateKey)
    rebindUnlockedSession(sessionToken, accountID)
    return
  }
  if (outcome.status === 'unlocked') {
    rebindUnlockedSession(sessionToken, accountID)
    return
  }
  if (outcome.status === 'none') lockVaultSessions()
}

type DirectUnlockOutcome =
  | {
      kind: 'v2'
      session: AccountUnlockSession
      identity: DeviceSigningIdentity | null
      privateKey: CryptoKey
    }
  | { kind: 'v1'; session: AccountVaultSession }

async function directUnlockOutcome(
  keyring: AnyAccountKeyring,
  accountID: string | null,
  sessionToken: string | null,
  secret: {
    password?: string
    recoveryKey?: string
    passkeyPrf?: Uint8Array
    loginSecret?: Uint8Array
  }
): Promise<DirectUnlockOutcome> {
  if (keyring.kind !== 'v2') {
    return {
      kind: 'v1',
      session: new AccountVaultSession(await unlockAccountKeyring(keyring, secret)),
    }
  }

  // A v2 password slot is trusted only after the password has passed account
  // authentication (confirmAccountPassword), which also owns old-slot migration.
  // Generic unlock may use recovery, login wrap, or passkey PRF, never a password.
  if (secret.password || (!secret.recoveryKey && !secret.passkeyPrf && !secret.loginSecret)) {
    throw new AccountVaultPasswordConfirmationRequiredError()
  }
  const privateKey = await unlockAccountKeyringV2(keyring, {
    recoveryKey: secret.recoveryKey,
    passkeyPrf: secret.passkeyPrf,
    loginSecret: secret.loginSecret,
  })
  return {
    kind: 'v2',
    session: new AccountUnlockSession(privateKey),
    identity: deviceIdentity(accountID, sessionToken, keyring),
    privateKey,
  }
}

async function persistDirectUnlockOutcome(
  outcome: DirectUnlockOutcome,
  alive: boolean,
  sessionToken: string | null
): Promise<void> {
  try {
    assertOperationCurrent(alive, sessionToken)
    if (outcome.kind === 'v2' && outcome.identity) {
      await saveDeviceSigningKey(outcome.identity, outcome.privateKey)
      assertOperationCurrent(alive, sessionToken)
    }
  } catch (error) {
    if (outcome.kind === 'v2' && outcome.identity) {
      await forgetDeviceSigningKey(outcome.identity)
    }
    outcome.session.lock()
    throw error
  }
}

function applyDirectUnlockOutcome(
  outcome: DirectUnlockOutcome,
  sessionToken: string | null,
  accountID: string | null
): void {
  lockVaultSessions()
  if (outcome.kind === 'v2') v2Session = outcome.session
  else v1Session = outcome.session
  rebindUnlockedSession(sessionToken, accountID)
}

type AccountPasswordConfirmation =
  | {
      kind: 'setup' | 'migration'
      keyring: AnyAccountKeyring | null
      accountID: string
      prepared: PreparedAccountVaultPassword
    }
  | {
      kind: 'unlocked'
      keyring: AnyAccountKeyring
      accountID: string
      v2: AccountUnlockSession | null
      v1: AccountVaultSession | null
      privateKey: CryptoKey | null
    }

async function confirmAccountVaultPassword(password: string): Promise<AccountPasswordConfirmation> {
  const [fetched, authSession] = await Promise.all([fetchAccountVault(), fetchAuthSession()])
  const keyring = readVaultFetch(fetched).keyring
  if (!keyring) {
    return {
      kind: 'setup',
      keyring: null,
      accountID: authSession.account_id,
      prepared: await prepareAccountVaultPassword(password),
    }
  }
  if (keyring.kind === 'v1') {
    return {
      kind: 'unlocked',
      keyring,
      accountID: authSession.account_id,
      v2: null,
      v1: new AccountVaultSession(await unlockAccountKeyring(keyring, { password })),
      privateKey: null,
    }
  }
  try {
    const privateKey = await unlockAccountKeyringV2(keyring, { password })
    return {
      kind: 'unlocked',
      keyring,
      accountID: authSession.account_id,
      v2: new AccountUnlockSession(privateKey),
      v1: null,
      privateKey,
    }
  } catch (error) {
    if (!(error instanceof AccountVaultSecretMismatchError)) throw error
    return {
      kind: 'migration',
      keyring,
      accountID: authSession.account_id,
      prepared: await prepareAccountVaultPassword(password),
    }
  }
}

function discardAccountPasswordConfirmation(outcome: AccountPasswordConfirmation): void {
  if (outcome.kind === 'unlocked') {
    outcome.v2?.lock()
    outcome.v1?.lock()
  } else {
    outcome.prepared.dispose()
  }
}

function assertAccountPasswordConfirmationCurrent(
  outcome: AccountPasswordConfirmation,
  alive: boolean,
  sessionToken: string | null
): void {
  try {
    assertOperationCurrent(alive, sessionToken)
  } catch (error) {
    discardAccountPasswordConfirmation(outcome)
    throw error
  }
}

async function persistConfirmationDeviceKey(
  outcome: Extract<AccountPasswordConfirmation, { kind: 'unlocked' }>,
  sessionToken: string | null,
  alive: boolean
): Promise<void> {
  const identity = deviceIdentity(outcome.accountID, sessionToken, outcome.keyring)
  if (!identity || !outcome.privateKey) return
  try {
    await saveDeviceSigningKey(identity, outcome.privateKey)
    assertOperationCurrent(alive, sessionToken)
  } catch (error) {
    await forgetDeviceSigningKey(identity)
    discardAccountPasswordConfirmation(outcome)
    throw error
  }
}

export function useAccountVault(): UseAccountVault {
  const [status, setStatus] = useAtom(statusAtom)
  const [keyringState, setKeyringState] = useAtom(keyringAtom)
  const [accountPasswordState, setAccountPasswordState] = useAtom(accountPasswordAtom)
  const { keyring, sessionToken: keyringSessionToken, accountID: keyringAccountID } = keyringState
  const alive = useRef(true)
  const reloadGeneration = useRef(0)

  useEffect(() => {
    alive.current = true
    return () => {
      alive.current = false
    }
  }, [])

  const reload = useCallback(async () => {
    const generation = ++reloadGeneration.current
    const sessionToken = getSessionToken()
    const knownAccount = getSessionAccountId()
    if (memoryUnlocked()) {
      if (unlockedAccountID && knownAccount && unlockedAccountID !== knownAccount) {
        lockVaultSessions()
      } else {
        rebindUnlockedSession(sessionToken, knownAccount)
      }
    }
    try {
      const [fetched, authSession] = await Promise.all([fetchAccountVault(), fetchAuthSession()])
      if (
        reloadIsStale(
          alive.current,
          generation,
          reloadGeneration.current,
          sessionToken,
          knownAccount
        )
      ) {
        const kept = keepSameAccountUnlock(knownAccount)
        if (kept) setStatus(kept)
        return kept ?? 'unknown'
      }
      const loadedFetch = readVaultFetch(fetched)
      const loaded = loadedFetch.keyring
      setKeyringState({
        keyring: loaded,
        sessionToken: getSessionToken() ?? sessionToken,
        accountID: authSession.account_id,
      })
      const outcome = await reloadVaultOutcome(
        loaded,
        authSession.account_id,
        getSessionToken() ?? sessionToken,
        loadedFetch.loginSecret
      )
      if (
        reloadIsStale(
          alive.current,
          generation,
          reloadGeneration.current,
          sessionToken,
          knownAccount
        )
      ) {
        const kept = keepSameAccountUnlock(authSession.account_id)
        if (kept) setStatus(kept)
        return kept ?? 'unknown'
      }
      applyReloadVaultOutcome(outcome, getSessionToken() ?? sessionToken, authSession.account_id)
      const kept = keepSameAccountUnlock(authSession.account_id)
      const next: AccountVaultStatus =
        outcome.status === 'restored' || kept ? 'unlocked' : outcome.status
      setStatus(next)
      if (next === 'unlocked' && !loadedFetch.loginSecret) await enrollLoginWrapIfNeeded(loaded)
      return next
    } catch {
      const stale = reloadIsStale(
        alive.current,
        generation,
        reloadGeneration.current,
        sessionToken,
        knownAccount
      )
      const kept = keepSameAccountUnlock(knownAccount)
      if (kept) {
        setStatus(kept)
        return kept
      }
      if (stale) return 'unknown'
      setStatus('offline')
      return 'offline'
    }
  }, [setKeyringState, setStatus])

  useEffect(() => {
    void reload()
  }, [reload])

  useEffect(
    () =>
      onSessionChanged(() => {
        const nextAccount = getSessionAccountId()
        const nextToken = getSessionToken()
        const previousAccount = keyringState.accountID ?? unlockedAccountID
        const switched = Boolean(previousAccount && nextAccount && previousAccount !== nextAccount)
        if (previousAccount && !nextAccount && !nextToken) {
          // The session ended (Log out, a refused session): the account's
          // keyring and unlocked key leave with it, so a guest cannot encrypt
          // to it or sign with it. The device key stays: it is this
          // account's, and the next login on this device unlocks with it.
          lockVaultSessions()
          discardPreparedAccountPassword()
          setAccountPasswordState({ sessionToken: null, purpose: 'none' })
          setKeyringState({ keyring: null, sessionToken: null, accountID: null })
          setStatus('unknown')
          return
        }
        if (!switched) {
          rebindUnlockedSession(nextToken, nextAccount)
          setKeyringState({
            keyring: keyringState.keyring,
            sessionToken: nextToken,
            accountID: nextAccount ?? keyringState.accountID,
          })
          if (memoryUnlocked()) {
            setStatus('unlocked')
            return
          }
          void reload()
          return
        }
        const previousIdentity = deviceIdentity(
          keyringState.accountID,
          keyringState.sessionToken,
          keyringState.keyring
        )
        if (previousIdentity) void forgetDeviceSigningKey(previousIdentity)
        lockVaultSessions()
        discardPreparedAccountPassword()
        setAccountPasswordState({ sessionToken: null, purpose: 'none' })
        setKeyringState({ keyring: null, sessionToken: nextToken, accountID: null })
        void reload()
      }),
    [keyringState, reload, setAccountPasswordState, setKeyringState, setStatus]
  )

  const setup = useCallback(
    async (recoveryKey: string) => {
      const sessionToken = getSessionToken()
      if (!accountPasswordReady(accountPasswordState, 'setup') || !preparedAccountPassword) {
        throw new Error('Account password is required to create wallet security')
      }
      const prepared = preparedAccountPassword
      const authSession = keyringAccountID ? null : await fetchAuthSession()
      const accountID = keyringAccountID ?? authSession?.account_id ?? null
      assertOperationCurrent(alive.current, sessionToken)
      if (!accountID) throw new Error('account session is unavailable')
      const minted = await createAccountKeyringV2WithPreparedPassword(prepared, recoveryKey)
      assertOperationCurrent(alive.current, sessionToken)
      await createAccountVault(minted.keyring)
      try {
        assertOperationCurrent(alive.current, sessionToken)
        const stored: AnyAccountKeyring = { kind: 'v2', ...minted.keyring }
        const identity = deviceIdentity(accountID, sessionToken, stored)
        if (identity) {
          await saveDeviceSigningKey(identity, minted.privateKey).catch(() => false)
          assertOperationCurrent(alive.current, sessionToken)
        }
        lockVaultSessions()
        v2Session = new AccountUnlockSession(minted.privateKey)
        rebindUnlockedSession(sessionToken, accountID)
        setKeyringState({ keyring: stored, sessionToken, accountID })
        setStatus('unlocked')
        discardPreparedAccountPassword()
        setAccountPasswordState({ sessionToken: null, purpose: 'none' })
      } catch {
        discardPreparedAccountPassword()
        setAccountPasswordState({ sessionToken: null, purpose: 'none' })
        throw new AccountVaultSetupCommittedError()
      }
    },
    [accountPasswordState, keyringAccountID, setAccountPasswordState, setKeyringState, setStatus]
  )

  const confirmAccountPassword = useCallback(
    async (password: string) => {
      // A session-change reload may still be fetching the same keyring. This
      // explicit confirmation owns the newer result and invalidates any stale
      // "locked" outcome that started before PBKDF2 completed.
      reloadGeneration.current += 1
      const sessionToken = getSessionToken()
      const outcome = await confirmAccountVaultPassword(password)
      assertAccountPasswordConfirmationCurrent(outcome, alive.current, sessionToken)
      setKeyringState({
        keyring: outcome.keyring,
        sessionToken,
        accountID: outcome.accountID,
      })

      if (outcome.kind !== 'unlocked') {
        discardPreparedAccountPassword()
        preparedAccountPassword = outcome.prepared
        setAccountPasswordState({ sessionToken, purpose: outcome.kind })
        setStatus(outcome.kind === 'setup' ? 'none' : 'locked')
        if (outcome.kind === 'migration') {
          throw new Error('account vault password migration required')
        }
        return
      }

      await persistConfirmationDeviceKey(outcome, sessionToken, alive.current)
      lockVaultSessions()
      v2Session = outcome.v2
      v1Session = outcome.v1
      rebindUnlockedSession(sessionToken, outcome.accountID)
      discardPreparedAccountPassword()
      setAccountPasswordState({ sessionToken: null, purpose: 'none' })
      setStatus('unlocked')
    },
    [setAccountPasswordState, setKeyringState, setStatus]
  )

  const migratePassword = useCallback(
    async (oldSecret: string | { password?: string; recoveryKey?: string }) => {
      const sessionToken = getSessionToken()
      if (
        keyring?.kind !== 'v2' ||
        keyringSessionToken !== sessionToken ||
        !accountPasswordReady(accountPasswordState, 'migration') ||
        !preparedAccountPassword
      ) {
        throw new Error('Account password is required to migrate wallet security')
      }
      const prepared = preparedAccountPassword
      const migrated = await rewrapAccountKeyringV2PasswordWithPrepared(
        keyring,
        typeof oldSecret === 'string' ? { password: oldSecret } : oldSecret,
        prepared
      )
      assertOperationCurrent(alive.current, sessionToken)
      try {
        await updateAccountVaultPasswordSlot(migrated.keyring, migrated.previousPasswordSlot)
      } catch (error) {
        discardPreparedAccountPassword()
        setAccountPasswordState({ sessionToken: null, purpose: 'none' })
        throw error
      }
      try {
        assertOperationCurrent(alive.current, sessionToken)
        const stored: AnyAccountKeyring = { kind: 'v2', ...migrated.keyring }
        const identity = deviceIdentity(keyringAccountID, sessionToken, stored)
        if (identity) {
          await saveDeviceSigningKey(identity, migrated.privateKey).catch(() => false)
          assertOperationCurrent(alive.current, sessionToken)
        }
        lockVaultSessions()
        v2Session = new AccountUnlockSession(migrated.privateKey)
        rebindUnlockedSession(sessionToken, keyringAccountID)
        setKeyringState({ keyring: stored, sessionToken, accountID: keyringAccountID })
        setStatus('unlocked')
        discardPreparedAccountPassword()
        setAccountPasswordState({ sessionToken: null, purpose: 'none' })
      } catch {
        discardPreparedAccountPassword()
        throw new AccountVaultMigrationCommittedError()
      }
    },
    [
      accountPasswordState,
      keyring,
      keyringAccountID,
      keyringSessionToken,
      setAccountPasswordState,
      setKeyringState,
      setStatus,
    ]
  )

  const setPasswordSlot = useCallback(
    async (newPassword: string, from?: DecryptSeedOptions): Promise<PasswordSlotResult> => {
      reloadGeneration.current += 1
      const sessionToken = getSessionToken()
      // Fresh from the server and kept in locals: nothing here reads the
      // atoms another call of this hook may have just written.
      const outcome = await confirmAccountVaultPassword(newPassword)
      assertAccountPasswordConfirmationCurrent(outcome, alive.current, sessionToken)
      setKeyringState({ keyring: outcome.keyring, sessionToken, accountID: outcome.accountID })
      if (outcome.kind === 'setup') {
        outcome.prepared.dispose()
        setStatus('none')
        return 'none'
      }
      if (outcome.kind === 'unlocked') {
        await persistConfirmationDeviceKey(outcome, sessionToken, alive.current)
        lockVaultSessions()
        v2Session = outcome.v2
        v1Session = outcome.v1
        rebindUnlockedSession(sessionToken, outcome.accountID)
        discardPreparedAccountPassword()
        setAccountPasswordState({ sessionToken: null, purpose: 'none' })
        setStatus('unlocked')
        return 'unlocked'
      }
      if (outcome.keyring?.kind !== 'v2') {
        outcome.prepared.dispose()
        return 'legacy'
      }
      if (!from) {
        // Keep the prepared password for the screen that asks for the secret
        // (migratePassword, ProfilePopover's form).
        discardPreparedAccountPassword()
        preparedAccountPassword = outcome.prepared
        setAccountPasswordState({ sessionToken, purpose: 'migration' })
        setStatus('locked')
        return 'needs-secret'
      }
      let migrated: Awaited<ReturnType<typeof rewrapAccountKeyringV2PasswordWithPrepared>>
      try {
        migrated = await rewrapAccountKeyringV2PasswordWithPrepared(
          outcome.keyring,
          from,
          outcome.prepared
        )
      } finally {
        outcome.prepared.dispose()
      }
      assertOperationCurrent(alive.current, sessionToken)
      await updateAccountVaultPasswordSlot(migrated.keyring, migrated.previousPasswordSlot)
      try {
        assertOperationCurrent(alive.current, sessionToken)
        const stored: AnyAccountKeyring = { kind: 'v2', ...migrated.keyring }
        const identity = deviceIdentity(outcome.accountID, sessionToken, stored)
        if (identity) {
          await saveDeviceSigningKey(identity, migrated.privateKey).catch(() => false)
          assertOperationCurrent(alive.current, sessionToken)
        }
        lockVaultSessions()
        v2Session = new AccountUnlockSession(migrated.privateKey)
        rebindUnlockedSession(sessionToken, outcome.accountID)
        setKeyringState({ keyring: stored, sessionToken, accountID: outcome.accountID })
        discardPreparedAccountPassword()
        setAccountPasswordState({ sessionToken: null, purpose: 'none' })
        setStatus('unlocked')
      } catch {
        throw new AccountVaultMigrationCommittedError()
      }
      return 'unlocked'
    },
    [setAccountPasswordState, setKeyringState, setStatus]
  )

  const rotateLoginSecret = useCallback(
    async (oldLoginSecret: Uint8Array) => {
      const sessionToken = getSessionToken()
      const [fetched, authSession] = await Promise.all([fetchAccountVault(), fetchAuthSession()])
      const loaded = readVaultFetch(fetched).keyring
      if (loaded?.kind !== 'v2' || !loaded.keySlots.some((slot) => slot.kind === 'login')) return
      assertOperationCurrent(alive.current, sessionToken)
      const rotated = await rotateAccountKeyringV2LoginSlot(loaded, oldLoginSecret)
      try {
        await rotateAccountLoginWrap({
          publicKeyB64: loaded.publicKeyB64,
          expectedLoginSlot: rotated.previousLoginSlot,
          loginSlot: rotated.loginSlot,
          loginSecret: rotated.loginSecret,
        })
      } finally {
        rotated.loginSecret.fill(0)
      }
      if (alive.current && sessionToken === getSessionToken()) {
        setKeyringState({
          keyring: { kind: 'v2', ...rotated.keyring },
          sessionToken,
          accountID: authSession.account_id,
        })
      }
    },
    [setKeyringState]
  )

  const unlock = useCallback(
    async (secret: {
      password?: string
      recoveryKey?: string
      passkeyPrf?: Uint8Array
      loginSecret?: Uint8Array
    }) => {
      const sessionToken = getSessionToken()
      let ring = keyring
      let accountID = keyringAccountID
      if (!ring || keyringSessionToken !== sessionToken) {
        const [fetched, authSession] = await Promise.all([fetchAccountVault(), fetchAuthSession()])
        ring = readVaultFetch(fetched).keyring
        accountID = authSession.account_id
        assertOperationCurrent(alive.current, sessionToken)
        if (!ring) throw new Error('account vault is not set up')
        setKeyringState({ keyring: ring, sessionToken, accountID })
      }
      const outcome = await directUnlockOutcome(ring, accountID, sessionToken, secret)
      await persistDirectUnlockOutcome(outcome, alive.current, sessionToken)
      applyDirectUnlockOutcome(outcome, sessionToken, accountID)
      if (alive.current) setStatus('unlocked')
      await enrollLoginWrapIfNeeded(ring)
    },
    [keyring, keyringAccountID, keyringSessionToken, setKeyringState, setStatus]
  )

  const unlockFromSession = useCallback(async () => {
    const accountID = getSessionAccountId()
    if (sameAccountMemoryUnlocked(accountID)) {
      rebindUnlockedSession(getSessionToken(), accountID)
      setStatus('unlocked')
      await enrollLoginWrapIfNeeded(keyring)
      return 'unlocked' as AccountVaultStatus
    }
    const sessionToken = getSessionToken()
    const [fetched, authSession] = await Promise.all([fetchAccountVault(), fetchAuthSession()])
    const loadedFetch = readVaultFetch(fetched)
    const loaded = loadedFetch.keyring
    if (!loaded) {
      setStatus('none')
      return 'none'
    }
    setKeyringState({
      keyring: loaded,
      sessionToken,
      accountID: authSession.account_id,
    })
    if (loadedFetch.loginSecret && loaded.kind === 'v2') {
      try {
        const outcome = await directUnlockOutcome(loaded, authSession.account_id, sessionToken, {
          loginSecret: loadedFetch.loginSecret,
        })
        await persistDirectUnlockOutcome(outcome, alive.current, sessionToken)
        applyDirectUnlockOutcome(outcome, sessionToken, authSession.account_id)
        if (alive.current) setStatus('unlocked')
        return 'unlocked'
      } catch {
        // Login wrap missed this keyring — restore from this device next.
      }
    }
    return reload()
  }, [keyring, reload, setKeyringState, setStatus])

  const lock = useCallback(async () => {
    lockVaultSessions()
    setStatus((prev) => (prev === 'unlocked' ? 'locked' : prev))
  }, [setStatus])

  const forgetDevice = useCallback(async () => {
    const identity = deviceIdentity(keyringAccountID, keyringSessionToken, keyring)
    lockVaultSessions()
    discardPreparedAccountPassword()
    setAccountPasswordState({ sessionToken: null, purpose: 'none' })
    setStatus((prev) => (prev === 'unlocked' ? 'locked' : prev))
    if (identity) await forgetDeviceSigningKey(identity)
  }, [keyring, keyringAccountID, keyringSessionToken, setAccountPasswordState, setStatus])

  // v2: public key is enough. v1 (legacy): the DEK must be unlocked first.
  const encryptSeed = useCallback(
    async (seedWords: string) => {
      if (keyring?.kind === 'v2') {
        if (keyringSessionToken !== getSessionToken()) {
          throw new Error('account vault is locked')
        }
        return encryptSeedToAccountVault(keyring.publicKeyB64, seedWords)
      }
      try {
        const session = requireCurrentUnlockedSession()
        if (!(session instanceof AccountVaultSession)) throw new Error('account vault is locked')
        return session.encryptSecret(seedWords)
      } catch (error) {
        setStatus((prev) => (prev === 'unlocked' ? 'locked' : prev))
        throw error
      }
    },
    [keyring, keyringSessionToken, setStatus]
  )

  const decryptSeed = useCallback(
    async (envelope: AccountSecretEnvelope) => {
      try {
        const session = requireCurrentUnlockedSession()
        return session instanceof AccountUnlockSession
          ? session.decryptSeed(envelope)
          : session.decryptSecret(envelope)
      } catch (error) {
        setStatus((prev) => (prev === 'unlocked' ? 'locked' : prev))
        throw error
      }
    },
    [setStatus]
  )

  return {
    status,
    canEncrypt:
      (keyring?.kind === 'v2' && keyringSessionToken === getSessionToken()) ||
      sessionUnlocked(getSessionToken()),
    canSign: sessionUnlocked(getSessionToken()),
    accountPasswordReady: accountPasswordReady(accountPasswordState, 'setup'),
    migrationRequired: accountPasswordReady(accountPasswordState, 'migration'),
    legacy: keyring?.kind === 'v1',
    reload,
    unlockFromSession,
    setup,
    confirmAccountPassword,
    migratePassword,
    setPasswordSlot,
    rotateLoginSecret,
    unlock,
    lock,
    forgetDevice,
    encryptSeed,
    decryptSeed,
  }
}
